// ============================================================================
// Remove Integration Dialog
// ============================================================================
// Confirmation dialog with credential_model-aware messaging.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { CredentialModel } from '@/types/integrations'

interface RemoveIntegrationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  providerName: string
  credentialModel: CredentialModel
  onConfirm: () => void
  isPending?: boolean
}

const MODEL_MESSAGES: Record<CredentialModel, { title: string; description: string }> = {
  platform: {
    title: 'Disconnect Integration',
    description:
      'This will disconnect all locations from {provider}. Your API credentials are managed by PlaneMAX and will be preserved. You can re-add this integration later without re-entering credentials.',
  },
  per_org: {
    title: 'Remove Integration',
    description:
      'This will remove {provider} and delete your API credentials. You will need to re-enter credentials to reconnect.',
  },
  per_location: {
    title: 'Remove Integration',
    description:
      'This will remove the {provider} connection and its credentials for this location.',
  },
}

export function RemoveIntegrationDialog({
  open,
  onOpenChange,
  providerName,
  credentialModel,
  onConfirm,
  isPending,
}: RemoveIntegrationDialogProps) {
  const msg = MODEL_MESSAGES[credentialModel]

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{msg.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {msg.description.replace('{provider}', providerName)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? 'Removing...' : credentialModel === 'platform' ? 'Disconnect' : 'Remove'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
